import {
  toolRunApprovedDossbotEventDataSchema,
  toolRunCanceledDossbotEventDataSchema,
  toolRunCompletedDossbotEventDataSchema,
  toolRunFailedDossbotEventDataSchema,
  toolRunRejectedDossbotEventDataSchema,
  toolRunStartedDossbotEventDataSchema,
} from '@doss/common-all'
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckIcon,
  ListChecksIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react'
import {
  AgentRun,
  DossbotChatRun,
  DossbotEventType,
  ToolRunStep,
} from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { AnimatedDossbotIcon } from '~/components/dossbot/AnimatedDossbotIcon'
import { EventHeader } from '~/components/dossbot/DossbotChatEvent'
import { ProgressEvent } from './DossbotChat'

interface EventProcessingResult {
  progressEvent?: ProgressEvent
  eventTitle?: { title: string; icon: JSX.Element }
  shouldAddPendingConfirmation?: boolean
  shouldRemovePendingConfirmation?: boolean
  updateEventTitle?: (current: { title: string; icon: JSX.Element }) => {
    title: string
    icon: JSX.Element
  }
}

interface ProcessEventParams {
  eventType: DossbotEventType
  eventPayload: unknown
  chatRun?: Pick<DossbotChatRun, 'id' | 'status' | 'response'> | null
  agentRun?: (Pick<AgentRun, 'id' | 'summary'> & { status: AgentRun['status'] | '' }) | null
  toolRunStep?: Pick<ToolRunStep, 'id' | 'confirmationMessage'> | null
}

/**
 * Processes a Dossbot event and returns instructions for state updates.
 * This utility is shared between historical event processing (DossbotChat)
 * and real-time subscription handling (DossbotChatEvent).
 */
export const useProcessEvent = () => {
  const { t } = useTypedTranslation()

  return (params: ProcessEventParams): EventProcessingResult => {
    const { eventType, eventPayload, chatRun, agentRun, toolRunStep } = params
    switch (eventType) {
      case DossbotEventType.CHAT_RUN_COMPLETED: {
        if (!chatRun?.response) {
          return {}
        }
        return {
          eventTitle: { title: t('dossbot.completed'), icon: <CheckIcon size={16} /> },
        }
      }

      case DossbotEventType.CHAT_RUN_FAILED: {
        return {
          progressEvent: {
            title: t('dossbot.error_message'),
            result: null,
            icon: AlertCircleIcon,
            type: DossbotEventType.CHAT_RUN_FAILED,
          },
          eventTitle: { title: t('dossbot.failed'), icon: <AlertCircleIcon size={16} /> },
        }
      }

      case DossbotEventType.CHAT_RUN_CANCELED: {
        return {
          progressEvent: {
            title: t('dossbot.canceled'),
            result: null,
            icon: XIcon,
            type: DossbotEventType.CHAT_RUN_CANCELED,
          },
          eventTitle: { title: t('dossbot.canceled'), icon: <XIcon size={16} /> },
        }
      }

      case DossbotEventType.TOOL_RUN_APPROVED: {
        if (!toolRunStep) {
          return {}
        }
        const parsedEvent = toolRunApprovedDossbotEventDataSchema.safeParse(eventPayload)
        if (!parsedEvent.success) {
          return {}
        }
        const approvedToolMessage = parsedEvent.data.approvedToolMessage
        return {
          progressEvent: {
            title: approvedToolMessage,
            result: null,
            icon: CheckIcon,
            type: DossbotEventType.TOOL_RUN_APPROVED,
          },
          shouldRemovePendingConfirmation: true,
        }
      }

      case DossbotEventType.TOOL_RUN_REJECTED: {
        if (!toolRunStep) {
          return {}
        }
        const parsedEvent = toolRunRejectedDossbotEventDataSchema.safeParse(eventPayload)
        if (!parsedEvent.success) {
          return {}
        }
        const rejectedToolMessage = parsedEvent.data.rejectedToolMessage
        return {
          progressEvent: {
            title: rejectedToolMessage,
            result: null,
            icon: XIcon,
            type: DossbotEventType.TOOL_RUN_REJECTED,
          },
          shouldRemovePendingConfirmation: true,
        }
      }

      case DossbotEventType.TOOL_RUN_STARTED: {
        const parsedEvent = toolRunStartedDossbotEventDataSchema.safeParse(eventPayload)
        if (!parsedEvent.success) {
          return {}
        }
        const runningToolMessage = parsedEvent.data.runningToolMessage
        return {
          progressEvent: {
            title: runningToolMessage,
            result: null,
            icon: ListChecksIcon,
            type: DossbotEventType.TOOL_RUN_STARTED,
          },
          updateEventTitle: (current: EventHeader) => ({
            title: current.title + '.',
            icon: current.icon,
          }),
        }
      }

      case DossbotEventType.TOOL_RUN_COMPLETED: {
        const parsedEvent = toolRunCompletedDossbotEventDataSchema.safeParse(eventPayload)
        if (!parsedEvent.success) {
          return {}
        }
        const completedToolMessage = parsedEvent.data.completedToolMessage
        return {
          progressEvent: {
            title: completedToolMessage,
            result: null,
            icon: CheckIcon,
            type: DossbotEventType.TOOL_RUN_COMPLETED,
          },
        }
      }

      case DossbotEventType.TOOL_RUN_FAILED: {
        const parsedEvent = toolRunFailedDossbotEventDataSchema.safeParse(eventPayload)
        if (!parsedEvent.success) {
          return {}
        }
        const failedToolMessage = parsedEvent.data.failedToolMessage
        return {
          progressEvent: {
            title: failedToolMessage,
            result: null,
            icon: AlertTriangleIcon,
            type: DossbotEventType.TOOL_RUN_FAILED,
          },
          eventTitle: { title: t('dossbot.failed'), icon: <AlertTriangleIcon size={16} /> },
        }
      }

      case DossbotEventType.TOOL_RUN_CANCELED: {
        const parsedEvent = toolRunCanceledDossbotEventDataSchema.safeParse(eventPayload)
        if (!parsedEvent.success) {
          return {}
        }
        const canceledToolMessage = parsedEvent.data.canceledToolMessage
        return {
          progressEvent: {
            title: canceledToolMessage,
            result: null,
            icon: XCircleIcon,
            type: DossbotEventType.TOOL_RUN_CANCELED,
          },
        }
      }

      case DossbotEventType.AGENT_RUN_COMPLETED: {
        if (!agentRun?.summary) {
          return {}
        }
        return {
          progressEvent: {
            title: agentRun.summary,
            result: null,
            icon: CheckIcon,
            type: DossbotEventType.AGENT_RUN_COMPLETED,
          },
        }
      }

      case DossbotEventType.TOOL_RUN_PENDING: {
        if (!toolRunStep?.confirmationMessage) {
          return {}
        }
        return {
          progressEvent: {
            title: t('dossbot.waiting_for_approval'),
            result: null,
            icon: AlertCircleIcon,
            type: DossbotEventType.TOOL_RUN_PENDING,
          },
          shouldAddPendingConfirmation: true,
        }
      }

      case DossbotEventType.CHAT_RUN_STARTED:
      case DossbotEventType.CHAT_SUMMARY_UPDATED:
      case DossbotEventType.CHAT_TITLE_UPDATED:
      case DossbotEventType.AGENT_RUN_STARTED:
      case DossbotEventType.AGENT_RUN_FAILED:
      case DossbotEventType.AGENT_RUN_CANCELED:
        return {}
    }
  }
}

/**
 * Creates the default "Thinking..." event title with animated Dossbot icon
 */
export function useCreateThinkingEventTitle() {
  const { t } = useTypedTranslation()

  return {
    title: t('dossbot.thinking'),
    icon: <AnimatedDossbotIcon height={16} width={16} />,
  }
}
